'use client';
import { isEqual } from 'es-toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SETTINGS_STORAGE_KEY_APP_INFORCFC } from '../settings-config';
import type { SettingsProviderProps } from '../types';

import { useSettingsManager } from './hooks/use-settings-manager';
import { SettingsContext } from './settings-context';

export function SettingsProvider({
  children,
  cookieSettings,
  defaultSettings,
  storageKey = SETTINGS_STORAGE_KEY_APP_INFORCFC,
}: SettingsProviderProps) {
  const isCookieEnabled = !!cookieSettings;
  const initialSettings = isCookieEnabled ? cookieSettings : defaultSettings;

  const { state, resetState, setField, setState } = useSettingsManager({
    storageKey,
    initialSettings,
  });

  const [openDrawer, setOpenDrawer] = useState(false);

  useEffect(() => {
    const muiColorScheme = localStorage.getItem('theme-mode');

    if (muiColorScheme) {
      const colorScheme = muiColorScheme === 'dark' ? 'dark' : 'light';

      setField('colorScheme', colorScheme);
    }
  }, [setField]);

  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const canReset = !isEqual(state, defaultSettings);

  const onReset = useCallback(() => {
    resetState(defaultSettings);
  }, [defaultSettings, resetState]);

  const memoizedValue = useMemo(
    () => ({
      canReset,
      onReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      state,
      setState,
      setField,
    }),
    [
      canReset,
      onReset,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
      state,
      setState,
      setField,
    ]
  );

  return (
    <SettingsContext.Provider value={memoizedValue}>
      {children}
    </SettingsContext.Provider>
  );
}
